import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import { canPieces } from "../lib/uom";

const CartContext = createContext(null);
// lines: {item_code: qty}; modes: {item_code: "piece"} for lines ordered in pieces (boxes otherwise)
const EMPTY = { lines: {}, modes: {}, localId: null, notes: "", date: null };

const load = (key) => {
  try {
    return { ...EMPTY, ...JSON.parse(localStorage.getItem(key) || "{}") };
  } catch (e) {
    return EMPTY;
  }
};

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const key = `vavlis.cart.${user.id}`;
  const [state, setState] = useState(() => load(key));

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(state));
  }, [key, state]);

  const setQty = useCallback((code, qty) => setState((s) => {
    const lines = { ...s.lines };
    const modes = { ...(s.modes || {}) };
    if (qty > 0) lines[code] = Math.min(qty, 9999);
    else { delete lines[code]; delete modes[code]; }
    return { ...s, lines, modes };
  }), []);

  const setMode = useCallback((code, mode) => setState((s) => ({ ...s, modes: { ...(s.modes || {}), [code]: mode } })), []);

  const addMany = useCallback((rows) => setState((s) => {
    const lines = { ...s.lines };
    const modes = { ...(s.modes || {}) };
    rows.forEach((r) => {
      const uom = r.uom || "box";
      if (lines[r.item_code] && (modes[r.item_code] || "box") !== uom) return; // keep the mode already in the cart
      lines[r.item_code] = Math.min((lines[r.item_code] || 0) + r.qty_boxes, 9999);
      modes[r.item_code] = uom;
    });
    return { ...s, lines, modes };
  }), []);

  const setMeta = useCallback((patch) => setState((s) => ({ ...s, ...patch })), []);
  const clear = useCallback(() => setState(EMPTY), []);

  // one idempotency key per checkout attempt; kept until SoftOne confirms
  const ensureLocalId = () => {
    if (state.localId) return state.localId;
    const id = crypto.randomUUID();
    setState((s) => ({ ...s, localId: id }));
    return id;
  };

  const value = useMemo(() => ({
    ...state, count: Object.keys(state.lines).length, qtyOf: (code) => state.lines[code] || 0,
    modeOf: (code, item) => (state.modes || {})[code] || (canPieces(item) ? "piece" : "box"),  // pieces by default when the item can be split
    setQty: (code, q, item) => { if (item && !(state.modes || {})[code]) setMode(code, canPieces(item) ? "piece" : "box"); setQty(code, q); },
    setMode, addMany, setMeta, clear, ensureLocalId,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [state, setQty, setMode, addMany, setMeta, clear]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
